import {ignoreReset, ignoreUndo} from "./undo";
import {pasteCondition} from "./conditional_formats";
import {pasteDropdown} from "./dropdown";
import {pasteFilter} from "./filter";
import {pasteSpan} from "./spans";
import {pasteValidation} from "./validation";
import { getStyle, updateStyleBorders } from "./styles";

let styledata, clipdata, origin, cutted, updateFilters, specialPaste, context;

export function init(view){
	view.attachEvent("onSheetRename", (name, newName)=> {
		if(context && context.page == name && context.view == view)
			context.page = newName;
	});

	view.attachEvent("onSheetRemove", (name)=> {
		if(context && context.page == name && context.view == view)
			context = null;
	});

	const clipboard = view.config.clipboard;
	const browserClipboard = navigator.clipboard;

	if(clipboard === false)
		return;

	webix.clipbuffer.init();
	const buffer = document.querySelector(".webix_clipbuffer");

	const keyC = 67; //keyboard 'c' code
	const keyX = 88; //keyboard 'x' code
	const grid = view.$$("cells");

	grid.attachEvent("onSelectChange", () => _sel_to_clip(view, buffer));

	grid.attachEvent("onKeyPress", (code, e) => {
		if((code === keyC || code === keyX) && (e.ctrlKey || e.metaKey) && grid.getSelectedId())
			setClipboard(view, (code === keyX)*1, buffer);
	});

	view.attachEvent("onCommand", action => {
		if(action.id.indexOf("paste") != -1){
			specialPaste = action.id.split("-")[1];

			if(browserClipboard && browserClipboard.readText){
				browserClipboard.readText()
					.then(text => {
						webix.clipbuffer.set(text);
						_clip_to_sel(view, grid, buffer.value);
					})
					.catch(()=> {
						_clip_to_sel(view, grid, clipdata);
					})
					.finally(()=> {
						specialPaste = null;
					});
			}
			else {
				_clip_to_sel(view, grid, clipdata);
				specialPaste = null;
			}
		}
		else if(action.id == "copy" || action.id == "cut"){
			setClipboard(view, (action.id == "cut")*1, buffer, browserClipboard, true);
		}
	});

	view.attachEvent("onUndo", (action, row, column, data) => {
		if (action == "paste"){
			const state = grid.getScrollState();
			const sel = grid.getSelectArea();
			// data loading resets undo history, and
			// we need to preserve it
			ignoreReset(()=>{
				view.parse(data);
				// hiding a column or row after paste can cause the selection to be lost, so it may be undefined afterward
				if(sel)
					grid.addSelectArea(sel.start, sel.end);
				grid.scrollTo(state.x, state.y);
			});
		}
	});

	if (!view.config.readonly) {
		grid.attachEvent("onPaste", text => {
			_clip_to_sel(view, grid, text);
		});
	}
}

function setClipboard(view, cut, buffer, browserClipboard, menu){
	if(!isClipboardAllowed(view)) return;

	const grid = view.$$("cells");
	cutted = cut;

	origin = grid.getSelectArea();
	
	styledata = _get_sel_style(view);
	context = { page: view.getActiveSheet(), view };

	if(menu && browserClipboard && browserClipboard.writeText)
		browserClipboard.writeText(buffer.value);

	clipdata = buffer.value;

	if(cutted && Object.keys(grid._ssFilters).length !== 0)
		updateFilters = allFiltersInZone(grid._ssFilters, view.config.columnCount);
}

function _sel_to_clip(view, buffer){
	if(view._loading_data) return;
	const grid = view.$$("cells");
	if(grid.getSelectedId())
		webix.delay(()=>{
			const data = [];

			const origin = grid.getSelectArea();
			for(let r = origin.start.row; r <= origin.end.row; r++){
				const row = [];
				for(let c = origin.start.column; c <= origin.end.column; c++)
					row.push(_updateDelimiter(view.getCellValue(r, c, false), view.config));
				data.push(row);
			}

			const clipdata = webix.csv.stringify(data, grid.config.delimiter);

			if (grid.getEditor()){
				buffer.value = clipdata;
			} else {
				buffer.focus();
				webix.clipbuffer.set(clipdata);
			}
			webix.UIManager.setFocus(grid);
		});
}

function _updateDelimiter(value, config) {
	const format = getClipboardNumberFormat(config);

	if(webix.rules.isNumber(value) && format)
		value = webix.Number.numToStr(format)(value);

	return value;
}

function isClipboardAllowed(view){
	if(Object.keys(view.$$("cells").getAllSelectAreas()).length !== 1){
		view.alert({
			text: webix.i18n.spreadsheet.labels["error-clipboard-multiselect"], type: "alert-error"
		});
		return false;
	}
	return true;
}

function _clip_to_sel(view, grid, text){
	if(!isClipboardAllowed(view)) return;

	if(!text)
		text = "";

	const nothingToPaste = !origin && specialPaste && specialPaste !== "values" && specialPaste !== "formulas" && specialPaste !== "transpose";
	if (nothingToPaste) return;

	const leftTop = grid.getSelectArea();
	if (!leftTop) return;
	const start = leftTop.start;
	const end = leftTop.end;

	const fromSheet = text === clipdata;

	let data = fromSheet ? styledata : webix.csv.parse(text, grid.config.delimiter);
	if(!fromSheet)
		data = _changeDelimiter(data, view.config);

	const translate = { id:"move", column:0, row: 0, cut:cutted };

	if (specialPaste || !fromSheet) {
		cutted = 0;
		updateFilters = 0;
	}

	if (view.callEvent("onBeforePaste", [start, end, data, fromSheet, context])) {
		const oldData = view.serialize();

		ignoreUndo(() => {
			if (specialPaste === "transpose") {
				data = data[0].map((_, ci) => data.map(row => webix.copy(row[ci])));
				_fixTransposedBorders(view, data);
			}

			const dataRowCount = data.length;
			const dataColCount = data[0].length;

			const colCount = end.column - start.column + 1;
			let rowCount = end.row - start.row + 1;

			_add_row_col(view, start, rowCount, data);

			if (fromSheet) {
				translate.column = start.column - origin.start.column;
				translate.row = start.row - origin.start.row;

				if (updateFilters){
					const sel = grid.getSelectArea();
					view.removeFilters();
					grid.addSelectArea(sel.start, sel.end);
				}

				const oldStyle = data.map((row, r) => row.map((col, c) => {
					const ri = start.row + r;
					const ci = start.column + c;
					const style = getStyle(view, { row: ri, column: ci }, context.page);
					return style;
				}));

				let mode = (!cutted && !updateFilters && (rowCount > dataRowCount || colCount > dataColCount)) ? 2 : 0;

				if (specialPaste != "conditions") {
					if (cutted == 1){
						const updated = view._mData.getCopyManager().move(
							view._mPage.cellID(start.row-1, start.column-1),
							context.view._mData.getStore().getMeta().$clipboard
						);
						_triggerCellChange(updated, context);
						
						if(context && !context.view.$destructed)
							context.view.groupUndo(()=>{
								for (let row = origin.start.row; row <= origin.end.row; row++) {
									for (let column = origin.start.column; column <= origin.end.column; column++) {
										context.view.setCellValue(row, column, null, context.page);
										context.view.setStyle(row, column, null, context.page);
									}
								}

								_restoreBordersAfterCut(context.view, context.page);
							});
					} else {
						if (specialPaste === "transpose") {
							// swapping dimensions of the original area to calculate the minimal target area for transposed paste
							const originRowSpan = origin.end.row - origin.start.row;
							const originColSpan = origin.end.column - origin.start.column;
							const targetEnd = {
								row: Math.max(start.row + originColSpan, end.row),
								column: Math.max(start.column + originRowSpan, end.column)
							};
							
							const updated = view._mData.getCopyManager().pasteTransposed(
								view._mPage.cellID(start.row - 1, start.column - 1),
								view._mPage.cellID(targetEnd.row - 1, targetEnd.column - 1),
								context.view._mData.getStore().getMeta().$clipboard,
								mode
							);
							_triggerCellChange(updated, context);
						} else {
							let handler;
							if (specialPaste) {
								handler = (a, b) => {
									switch(specialPaste){
										case "formulas":
											return webix.extend(a, { value: b.formula || b.value }, true);
										case "styles":
											return webix.extend(a, {style: b.style, format: b.format }, true);
										case "values":
											return webix.extend(a, {value: b.value }, true);
										case "validation":
											return webix.extend(a, {validation: b.validation }, true);
									}
								};
							}

							const updated = view._mData.getCopyManager().paste(
								view._mPage.cellID(start.row-1, start.column-1),
								view._mPage.cellID(
									Math.max(start.row + origin.end.row - origin.start.row, end.row)-1,
									Math.max(start.column + origin.end.column - origin.start.column, end.column)-1
								),
								context.view._mData.getStore().getMeta().$clipboard,
								mode,
								handler
							);
							_triggerCellChange(updated, context);
						}
					}
				}

				if (mode == 2) {
					for(let r = 0, ri = 0; r < Math.max(rowCount, dataRowCount); r++, ri++){
						if(ri == data.length) ri = 0;
						for (let c = 0, ci = 0; c < Math.max(colCount, dataColCount); c++, ci++){
							if(ci == data[0].length) ci = 0;

							const subtrans = {
								id: "move",
								column: translate.column + Math.floor(c/dataColCount) * dataColCount,
								row: translate.row + Math.floor(r/dataRowCount) * dataRowCount,
								cut: cutted
							};

							_clipboardToTable(view, start.row + r, start.column + c, data[ri][ci], subtrans, oldStyle[ri][ci]);
						}
					}
				} else {
					grid.mapCells(
						start.row,
						start.column,
						dataRowCount,
						null,
						function(value, row, col, row_ind, col_ind) {
							if (data[row_ind] && data[row_ind].length > col_ind) {
								const cdata = data[row_ind][col_ind];
								_clipboardToTable(view, row, col, cdata, translate, oldStyle[row_ind][col_ind]);
							}
						},
						true
					);
				}
				if(cutted == 1){
					//cut and cleared
					cutted = 2;
					updateFilters = false;
				}
			} else {
				for (let r = 0, ri = 0; r < Math.max(rowCount, dataRowCount); r++, ri++) {
					if (ri == data.length) ri = 0;
					for (var c = 0, ci = 0; c < Math.max(colCount, dataColCount); c++, ci++) {
						if (ci == data[0].length) ci = 0;
						view.setCellValue(start.row + r, start.column + c, data[ri][ci]);
					}
				}
			}
		}, view);

		view.callEvent("onAction", ["paste", {
			value: oldData,
			newValue: view.serialize()
		}]);
		view.callEvent("onCommand", [{id:"toolbar-update"}]);
		view.refresh();
	}
}

function _triggerCellChange(updated, context){
	if (context && !context.view.$destructed) {
		updated.forEach(cell => {
			const [row, column, data] = cell;
			if (data.value) {
				context.view.callEvent("onCellChange", [row+1, column+1, data.value, context.page]);
			}
		});
	}
}

function _stylesChanged(ns, os){
	if (!ns && !os) return false;
	if (!ns || !os) return true;
	return ns.text !== os.text;
}

function _triggerCellEvents(view, row, column, data, oldStyle){
	// style present or special paste style
	if (_stylesChanged(data.style, oldStyle)) {
		view.callEvent("onStyleChange", [ row, column, data.style, oldStyle, context.page ]);
		const format = data.style && data.style.props.format || null;
		const oldFormat = oldStyle && oldStyle.props.format || null;
		if (format != oldFormat)
			view.callEvent("onFormatChange", [ row, column, format, oldFormat, context.page ]);
	}
}

function needCopyStyles(sp){
	return !sp || sp == "styles" || sp == "transpose";
}

function _clipboardToTable(view, row, col, cdata, translate, oldStyle){
	if (typeof cdata === "object"){
		const sameView = context && context.view == view;
		if (!(sameView && context.page == view.getActiveSheet())) {
			const cs = needCopyStyles(specialPaste);
			if (cdata.style && cdata.style.props && cs){
				const style = view.addStyle(cdata.style.props);
				view.setStyle(row, col, style);
			}
		}

		const extra = cdata.extra;
		const copyAll = !specialPaste || specialPaste == "transpose";
		if (extra) {
			if (extra.condition && (copyAll || specialPaste == "conditions"))
				pasteCondition(view, extra, row, col, cutted);
			if (extra.dropdown && copyAll)
				pasteDropdown(view, extra, row, col, cutted, translate);
			if (extra.filter && updateFilters)
				pasteFilter(view, extra, row, col, cutted, translate);
			if (extra.validation && (copyAll || specialPaste == "validation"))
				pasteValidation(view, extra, row, col, cutted, translate);
		}

		if (cdata.style && cdata.style.props && sameView && needCopyStyles(specialPaste)){
			let pasteStyle = cdata.style;
			if (view.config.borderCollapse) {
				const updatedStyle = updateStyleBorders(view, row, col, pasteStyle);
				if (updatedStyle)
					view.setStyle(row, col, updatedStyle);
			}
			else if (specialPaste === "transpose") {
				const collapsed = _collapseMatchingBorders(view, row, col, pasteStyle);
				if (collapsed)
					pasteStyle = collapsed;
			}

			// restoring "lost" top and left borders of the copied cells (row 1 and col 1)
			const currentStyle = view.getStyle(row, col);
			if (!currentStyle || currentStyle.id !== pasteStyle.id)
				view.setStyle(row, col, pasteStyle);
		}

		if (!specialPaste)
			pasteSpan(view, extra, row, col, cutted, translate);

		view.saveCell(row, col, view.getActiveSheet());

		_triggerCellEvents(view, row, col, cdata, oldStyle);
	}
}

function _restoreBordersAfterCut(view, page){
	const dataRows = styledata.length;
	const dataCols = styledata[0].length;
	const belowRow = origin.end.row + 1;
	const rightCol = origin.end.column + 1;

	// restore border-top on the row below the cut range
	for(let c = 0; c < dataCols; c++){
		const lastRowCell = styledata[dataRows - 1][c];
		const borderBottom = lastRowCell.style && lastRowCell.style.props["border-bottom"];
		if(borderBottom){
			const col = origin.start.column + c;
			const cellStyle = view.getStyle(belowRow, col, page);
			if(_hasBorders(cellStyle)){
				const newStyle = view.addStyle({"border-top": borderBottom}, cellStyle, page);
				view.setStyle(belowRow, col, newStyle, page);
			}
		}
	}

	// restore border-left on the column to the right of the cut range
	for(let r = 0; r < dataRows; r++){
		const lastColCell = styledata[r][dataCols - 1];
		const borderRight = lastColCell.style && lastColCell.style.props["border-right"];
		if(borderRight){
			const row = origin.start.row + r;
			const cellStyle = view.getStyle(row, rightCol, page);
			if(_hasBorders(cellStyle)){
				const newStyle = view.addStyle({"border-left": borderRight}, cellStyle, page);
				view.setStyle(row, rightCol, newStyle, page);
			}
		}
	}
}

function _collapseMatchingBorders(view, row, col, style){
	const props = style.props;
	let collapsed = null;

	if(props["border-top"]){
		const aboveStyle = view.getStyle(row - 1, col);
		if(aboveStyle && aboveStyle.props["border-bottom"] === props["border-top"]){
			if(!collapsed) collapsed = {};
			collapsed["border-top"] = "";
		}
	}
	if(props["border-left"]){
		const leftStyle = view.getStyle(row, col - 1);
		if(leftStyle && leftStyle.props["border-right"] === props["border-left"]){
			if(!collapsed) collapsed = {};
			collapsed["border-left"] = "";
		}
	}

	if(collapsed)
		return view.addStyle(collapsed, style);
}

function _fixTransposedBorders(view, data){
	// borders don't rotate — horizontal stays horizontal, vertical stays vertical
	// after transpose, cells may lose their visual top/left borders when mixed-border
	// selections are rearranged; restore by borrowing from same-direction neighbors
	for(let r = 0; r < data.length; r++){
		for(let c = 0; c < data[r].length; c++){
			const cell = data[r][c];
			if(!cell.style || !_hasBorders(cell.style)) continue;

			if(c >= 1 && !cell.style.props["border-top"]){
				const above = r > 0 ? data[r - 1][c] : null;
				if(!above || !above.style || !above.style.props["border-bottom"]){
					const val = data[r][c - 1].style && data[r][c - 1].style.props["border-bottom"];
					if(val) cell.style = view.addStyle({"border-top": val}, cell.style);
				}
			}

			if(r >= 1 && !cell.style.props["border-left"]){
				const left = c > 0 ? data[r][c - 1] : null;
				if(!left || !left.style || !left.style.props["border-right"]){
					const val = data[r - 1][c].style && data[r - 1][c].style.props["border-right"];
					if(val) cell.style = view.addStyle({"border-left": val}, cell.style);
				}
			}
		}
	}
}

function _getAdjacentBorder(view, row, col, borderProp){
	const span = view.getSpan(row, col);
	let targetRow = row;
	let targetCol = col;

	if(span){
		if(borderProp === "border-bottom")
			targetRow = span[0]*1 + span[3] - 1;
		else if(borderProp === "border-right")
			targetCol = span[1]*1 + span[2] - 1;
	}

	const style = view.getStyle(targetRow, targetCol);
	return style && style.props[borderProp] || null;
}

function _hasBorders(style){
	if(!style || !style.props) return false;
	const p = style.props;
	return !!(p["border-top"] || p["border-bottom"] || p["border-left"] || p["border-right"]);
}

function _get_sel_style(view){
	const data = [];

	const cm = view._mData.getCopyManager();
	cm.copy(
		view._mPage.cellID(origin.start.row-1, origin.start.column-1),
		view._mPage.cellID(origin.end.row-1, origin.end.column-1)
	);

	for(let r = origin.start.row; r <= origin.end.row; r++){
		const row = [];
		for(let c = origin.start.column; c <= origin.end.column; c++){
			let math = view.getCellValue(r, c);
			math = math && math[0] == "=" && math.length > 1 ? math : null;

			let style = view.getStyle(r, c);

			if(style){
				const props = style.props;
				let expanded = null;

				if(!props["border-top"] && r == origin.start.row && r > 1){
					const val = _getAdjacentBorder(view, r - 1, c, "border-bottom");
					if(val){
						if(!expanded) expanded = {};
						expanded["border-top"] = val;
					}
				}
				if(!props["border-left"] && c == origin.start.column && c > 1){
					const val = _getAdjacentBorder(view, r, c - 1, "border-right");
					if(val){
						if(!expanded) expanded = {};
						expanded["border-left"] = val;
					}
				}
				if(expanded)
					style = view.addStyle(expanded, style);
			}

			const obj = { text: view.getCellValue(r, c, false), math, style };

			const condition = view.conditions.get(r, c);
			const editor = view.getCellEditor(r, c);
			const filter = view.getCellFilter(r, c);
			const span = view._table.getSpan(r, c);
			const validation = view.validation.get(r, c);

			if (editor || filter || condition || span || validation){
				const extra = { row:r, col:c };
				if (condition) extra.condition = condition;
				if (editor) extra.dropdown = editor;
				if (filter) extra.filter = filter;
				if (span) extra.span = span;
				if(validation) extra.validation = validation;
				obj.extra = extra;
			}

			row.push(obj);
		}
		data.push(row);
	}

	return data;
}

function allFiltersInZone(extra, columnCount){
	var filters = Object.keys(extra);
	var startRow = Math.min.apply(null, filters);
	var endRow = Math.max.apply(null, filters);
	var startCol = columnCount;
	var endCol = 1;

	for (var i = startRow; i <= endRow; i++){
		if(extra[i]){
			var columns = Object.keys(extra[i]);
			startCol = Math.min(startCol, Math.min.apply(null, columns));
			endCol = Math.max(endCol, Math.max.apply(null, columns));
		}
	}

	if(origin.start.row <= startRow && origin.end.row >= endRow && origin.start.column <= startCol && origin.end.column >= endCol)
		return true;
	else
		return false;
}
//dynamically increase rows and columns according to pasted data size
function _add_row_col(view, start, rowCount, data){
	var maxCol = start.column + data[0].length - 1;
	let maxRow;

	if(rowCount > data.length){
		let extraSpanRows = 0;
		const lastRows = rowCount % data.length;
		for (let r = 0; r < lastRows; r++) {
			for(let c = 0; c < data[r].length; c++){
				if(data[r][c].extra && data[r][c].extra.span){
					const span = data[r][c].extra.span;
					if(span[0] == origin.start.row + r && span[1] == origin.start.column + c){
						const extra = r + span[3] - lastRows;
						if(extra > 0)
							extraSpanRows = Math.max(extra, extraSpanRows);
					}
				}
			}
		}
		maxRow = start.row + rowCount + extraSpanRows - 1;
	}
	else
		maxRow = start.row + data.length - 1;

	if(maxRow > view.config.rowCount || maxCol > view.config.columnCount){
		var action = { id:"add" };
		if (maxRow > view.config.rowCount) {
			action.group = "row";
			view.callEvent("onCommand", [
				action, 
				{ row: view.config.rowCount + 1 }, 
				{ row: maxRow }
			]);
		}
		if (maxCol > view.config.columnCount) {
			action.group = "column";
			view.callEvent("onCommand", [
				action, 
				{ column: view.config.columnCount + 1 },
				{ column: maxCol }
			]);
		}
	}
}

function _changeDelimiter(data, config){
	const format = getClipboardNumberFormat(config);

	if(format)
		for(let i = 0; i < data.length; i++)
			for(let k = 0; k < data[i].length; k++){
				const record = webix.Number.parse(data[i][k], format);
				if(webix.rules.isNumber(record))
					data[i][k] = record;
			}

	return data;
}

function getClipboardNumberFormat(config){
	let format = config.clipboardNumberFormat;

	if(!format && config.clipboardDecimalDelimiter) // backward compatibility
		format = {
			decimalDelimiter: config.clipboardDecimalDelimiter,
			decimalOptional: true
		};

	if(format)
		webix.extend(format, {
			minusSign: webix.i18n.minusSign,
			minusPosition: webix.i18n.minusPosition,
		});

	return format;
}
